import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ContentState, Editor, EditorState } from 'draft-js';
import 'draft-js/dist/Draft.css';
import TwitterText from 'twitter-text';

import Counter from './Counter';
import { TwitterDecorator, LinkedinDecorator } from './strategies';
import { EditorWrapper } from './Styled';

export interface EditorInputProps {
  className?: string;
  placeholder?: string;
  content?: string;
  onChange?: (content: string) => void;
  maxCharacters?: number;
  editorRef?: React.RefObject<Editor>;
  disabled?: boolean;
  isTwitter: boolean;
}

const EditorInput: React.FC<EditorInputProps> = React.memo(props => {
  const {
    placeholder,
    content = '',
    onChange,
    maxCharacters,
    className = '',
    editorRef,
    disabled = false,
    isTwitter
  } = props;

  const internalEditorRef = useRef<Editor>(null);
  const finalEditorRef = editorRef || internalEditorRef;

  const decorator = React.useMemo(
    () => (isTwitter ? TwitterDecorator() : LinkedinDecorator()),
    [isTwitter]
  );

  const [editorState, setEditorState] = useState(() => {
    const initialContent = content.trim()
      ? ContentState.createFromText(content)
      : ContentState.createFromText('');
    return EditorState.createWithContent(initialContent, decorator);
  });

  useEffect(() => {
    const currentText = editorState.getCurrentContent().getPlainText('');
    if (content !== currentText) {
      const newContent = content.trim()
        ? ContentState.createFromText(content)
        : ContentState.createFromText('');
      setEditorState(EditorState.createWithContent(newContent, decorator));
    }
  }, [content, decorator]);

  const handleChange = useCallback(
    (newEditorState: EditorState) => {
      const currentContent = newEditorState
        .getCurrentContent()
        .getPlainText('');
      setEditorState(newEditorState);
      onChange?.(currentContent);
    },
    [onChange]
  );

  const focusEditor = useCallback(() => {
    if (!disabled) {
      finalEditorRef.current?.focus();
    }
  }, [disabled, finalEditorRef]);

  const calculateCount = (currentText: any) => {
    const urlPattern = /(?:https?|ftp):\/\/\S+|www\.\S+/g;
    const urls = currentText.match(urlPattern) || [];
    const urlWeight = 33;
    return (
      currentText.replace(urlPattern, ' ').length + urls.length * urlWeight
    );
  };

  const currentText = editorState.getCurrentContent().getPlainText('');
  const characterCount = isTwitter
    ? TwitterText.getTweetLength(currentText)
    : calculateCount(currentText);
  const isOverLimit = maxCharacters ? characterCount > maxCharacters : false;

  return (
    <EditorWrapper
      className={`${className} ${isOverLimit ? 'over-limit' : ''}`}
      onClick={focusEditor}
      disabled={disabled}
      aria-disabled={disabled}
    >
      <Editor
        ref={finalEditorRef}
        editorState={editorState}
        onChange={handleChange}
        placeholder={placeholder}
        readOnly={disabled}
      />
      {maxCharacters && (
        <Counter
          isTwitter={isTwitter}
          maxCharacters={maxCharacters}
          text={currentText}
          isOverLimit={isOverLimit}
        />
      )}
    </EditorWrapper>
  );
});

EditorInput.displayName = 'EditorInput';

export default EditorInput;
